import React, { useRef, forwardRef, useImperativeHandle } from 'react';
import {
  useQuery,
  TextInput,
  NullableBooleanInput,
  SelectInput,
  RadioButtonGroupInput,
  useNotify,
  required,
  useMutation
} from 'react-admin';
import { FormSpy } from 'react-final-form';
import { Grid } from '@material-ui/core/';
import { makeStyles } from '@material-ui/core';
import omit from 'lodash/omit';
import moment from 'moment';

import DateInput from '../../../core/components/forked/input/DateInput';
import { isValidDate } from '../../../core/utils/index';
import EditToolbar from '../../../core/components/edit/EditToolbar';
import useNotifyServerError from '../../../core/hooks/useNotifyServerError';
import CustomSimpleForm from '../form/CustomSimpleForm';

const useStyles = makeStyles({
  checkboxGroup: {
    marginTop: '0'
  },
  label: {
    fontSize: '14px',
    transform: 'none',
    color: '#757575'
  }
});

const formatCheckboxValue = value => {
  // converting tRuE/FalSe to lower case since NullableBooleanInput is case sensitive
  if (value) return value.toLowerCase();
  return '';
};

const formatSelectInputValue = (possibleValues, value) => {
  value = value.toLowerCase();
  for (var i = 0; i < possibleValues.length; i++) {
    var nonModifiedCase = possibleValues[i].data;
    var valueToLowerCase = possibleValues[i].data.toLowerCase();
    if (valueToLowerCase === value) {
      if (nonModifiedCase === 'True') return 'Yes';
      if (nonModifiedCase === 'False') return 'No';
      return nonModifiedCase;
    }
  }
  return '';
};

const formatDate = value => {
  if (value) {
    return moment(value).format('YYYY-MM-DD');
  }
};

const UdfField = props => {
  const { index, udfField, type } = props;
  const classes = useStyles();

  if (
    props.udfField &&
    props.udfField.userUdfValue &&
    props.udfField.userUdfValue.value &&
    (udfField.fieldType === 'SELECT' || udfField.fieldType === 'RADIO')
  ) {
    props.udfField.userUdfValue.value = formatSelectInputValue(
      props.udfField.possibleValues,
      props.udfField.userUdfValue.value
    );
  }

  const inputProps = {
    source: `udfFields[${index}].${type}UdfValue.value`,
    label: udfField.fieldName,
    id: udfField.fieldName,
    variant: 'standard',
    helperText: false,
    validate: udfField.required ? [required()] : null
  };

  return (
    (udfField.fieldType === 'CHECKBOX' && (
      <NullableBooleanInput
        {...inputProps}
        fullWidth={true}
        format={formatCheckboxValue}
        parse={formatCheckboxValue}
      />
    )) ||
    (udfField.fieldType === 'TEXT' && (
      <TextInput {...inputProps} options={{ autoComplete: 'off' }} />
    )) ||
    (udfField.fieldType === 'DATE_FIELD' && (
      <DateInput
        {...inputProps}
        placeholder="MM/dd/yyyy"
        customParseValue={formatDate}
        validate={udfField.required ? [required(), isValidDate] : [isValidDate]}
      />
    )) ||
    (udfField.fieldType === 'SELECT' && (
      <SelectInput
        {...inputProps}
        choices={udfField.possibleValues}
        optionValue="data"
        optionText="data"
        allowEmpty={!udfField.required}
      />
    )) ||
    (udfField.fieldType === 'RADIO' && (
      <RadioButtonGroupInput
        {...inputProps}
        choices={udfField.possibleValues}
        optionValue="data"
        optionText="data"
        className={classes.checkboxGroup}
        classes={{
          label: classes.label
        }}
      />
    ))
  );
};

const UdfSection = props => {
  const { udfFields, type } = props;

  return (
    <Grid style={{ width: '100%' }} container spacing={1}>
      {udfFields.map((udfField, index) => (
        <Grid item xs={12} md={6} key={index}>
          <UdfField index={index} udfField={udfField} type={type} />
        </Grid>
      ))}
    </Grid>
  );
};

const UdfTab = forwardRef((props, ref) => {
  const {
    id,
    queryId,
    type,
    hasEditPermission,
    onChangeUDFs,
    hideCommands,
    setHasUdfs,
    ...rest
  } = props;
  const notify = useNotify();
  const [mutate] = useMutation();
  const notifyServerError = useNotifyServerError();
  let queryFilter = {};
  if (Array.isArray(id)) {
    queryId.forEach((key, index) => {
      queryFilter[key] = id[index];
    });
  } else {
    queryFilter = { [queryId]: id };
  }

  const formRef = useRef();

  useImperativeHandle(ref, () => ({
    saveUdfs(onSuccess) {
      handleSave(formRef.current.getState().values, onSuccess);
    }
  }));

  const handleSave = (values, onSuccess) => {
    const data = values.udfFields
      .filter(e => e[`${type}UdfValue`] && e[`${type}UdfValue`].value)
      .map(udf => {
        return {
          ...queryFilter,
          udfId: udf.udfId,
          value: udf[`${type}UdfValue`].value
        };
      });
    if (!data || data.length === 0) {
      // No udfs no needs to save call on success
      if (onSuccess) onSuccess();
      return;
    }
    let saveId = id;
    if (Array.isArray(saveId)) {
      saveId = saveId.join(':');
    }

    mutate(
      {
        type: 'updateMany',
        resource: `${type.toLowerCase()}udfs`,
        payload: {
          id: saveId,
          data: data
        }
      },
      {
        onSuccess: response => {
          //initialize form again with updated values so toolbar buttons are properly enabled/disabled and form pristine
          const updatedUdfs = formRef.current
            .getState()
            .values.udfFields.map(udf => {
              let updatedUdf = { ...udf };
              const udfNewValue = response.data.find(
                e => e.udfId === udf.udfId
              );
              if (udfNewValue) {
                updatedUdf[`${type}UdfValue`] = {
                  ...updatedUdf[`${type}UdfValue`],
                  value: udfNewValue.value,
                  lastModified: udfNewValue.lastModified
                };
              }
              return updatedUdf;
            });
          formRef.current.initialize({ udfFields: updatedUdfs });
          notify('ra.notification.udfs_successfully_updated');
          if (onSuccess) {
            onSuccess();
          }
        },
        onFailure: response => {
          notify(response.message, 'error');
        }
      }
    );
  };

  const { data, error } = useQuery({
    type: 'getList',
    resource: `${type.toLowerCase()}udfs`,
    payload: {
      filter: queryFilter
    }
  });
  if (error) notifyServerError(error);
  if (!data) return null;
  //filter out udf fields without possible values
  const ufdFieldsWithPossibleValues = data.filter(
    field =>
      !(
        (field.fieldType === 'SELECT' || field.fieldType === 'RADIO') &&
        field.possibleValues.length === 0
      )
  );
  if (ufdFieldsWithPossibleValues.length > 0 && setHasUdfs) {
    setHasUdfs(true);
  }

  const toolbar = hideCommands ? null : (
    <EditToolbar
      {...omit(rest, [
        'userphonetypeIds',
        'useraddresstypeIds',
        'userType',
        'documentTypes',
        'crudGetList',
        'hasCreatePermission',
        'classes',
        'variant'
      ])}
      hasEditPermission={hasEditPermission}
      deletable={false}
    />
  );

  return (
    <CustomSimpleForm
      initialValues={{ udfFields: ufdFieldsWithPossibleValues }}
      margin="normal"
      save={handleSave}
      initialValuesEqual={() => true}
      toolbar={toolbar}
    >
      <FormSpy>
        {newFormProps => {
          formRef.current = newFormProps.form;
          return (
            <UdfSection udfFields={ufdFieldsWithPossibleValues} type={type} />
          );
        }}
      </FormSpy>
      {onChangeUDFs && (
        <FormSpy
          subscription={{ dirty: true, valid: true }}
          onChange={props => {
            // this was called on render and makes React unhappy (before timeout)
            setTimeout(() => onChangeUDFs(props.dirty, props.valid));
          }}
        />
      )}
    </CustomSimpleForm>
  );
});

export default UdfTab;
